import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  FileText, Tag, Star, Clock, Settings,
  Trash, Home, Plus,
  ChevronDown, ChevronRight, Users, Package, Cog, PanelLeftClose, PanelLeft, HelpCircle, Layers3
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { useCategoryStore } from '../../stores/category-store';
import UploadModal from '../files/UploadModal';
import CreateCategoryModal from '../categories/CreateCategoryModal';
import { useI18n } from '../../i18n';
import { useUserStore } from '../../stores/user-store';
import type { Category } from '../../types';
import { isSuperAdmin } from '../../utils/permissions';

interface SidebarNewProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  width: number;
  onWidthChange: (width: number) => void;
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
}

// 宽度限制
const MIN_WIDTH = 180;
const MAX_WIDTH = 400;
const COLLAPSE_THRESHOLD = 120; // 拖到这个宽度以下自动折叠
const COLLAPSED_WIDTH = 64;

const SidebarLink: React.FC<{
  to: string;
  icon: React.ReactNode;
  label: string;
  collapsed: boolean;
  end?: boolean;
}> = ({ to, icon, label, collapsed, end = false }) => (
  <NavLink
    to={to}
    end={end}
    className={({ isActive }) => cn(
      'group flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors',
      isActive
        ? 'bg-sky-50 text-sky-600'
        : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700',
      collapsed ? 'justify-center px-2' : ''
    )}
    title={collapsed ? label : undefined}
  >
    <span className="group-hover:text-sky-500 transition-colors flex-shrink-0">
      {icon}
    </span>
    {!collapsed && <span className="truncate">{label}</span>}
  </NavLink>
);

const SidebarNew: React.FC<SidebarNewProps> = ({ 
  collapsed, 
  onToggleCollapse,
  width,
  onWidthChange,
  mobileOpen = false 
}) => {
  const location = useLocation();
  const { categories, expandedCategories, toggleCategoryExpansion, fetchCategories } = useCategoryStore();
  const { profile: currentUser } = useUserStore();
  const canManageFaqSpaces = currentUser
    ? isSuperAdmin(currentUser.role, currentUser.can_access_all_tenants)
    : false;
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCreateCategoryModal, setShowCreateCategoryModal] = useState(false);
  const [categoriesExpanded, setCategoriesExpanded] = useState(true);
  const [systemSettingsExpanded, setSystemSettingsExpanded] = useState(false);
  const { t } = useI18n();

  // 拖拽状态
  const [isResizing, setIsResizing] = useState(false);
  const [isHoveringResizer, setIsHoveringResizer] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // 开始拖拽
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = width;
  }, [width]);

  // 拖拽过程
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - startXRef.current;
      const newWidth = startWidthRef.current + delta;
      
      if (newWidth < COLLAPSE_THRESHOLD) {
        // 拖到很小时自动折叠
        if (!collapsed) {
          onToggleCollapse();
        }
        setIsResizing(false);
      } else if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
        if (collapsed) {
          // 从折叠状态拖出来
          onToggleCollapse();
        }
        onWidthChange(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, collapsed, onToggleCollapse, onWidthChange]);

  // 双击分隔线重置宽度
  const handleDoubleClick = useCallback(() => {
    if (collapsed) {
      onToggleCollapse();
    } else {
      onWidthChange(240); // 重置为默认宽度
    }
  }, [collapsed, onToggleCollapse, onWidthChange]);

  const renderCategories = (cats: Category[], level = 0) => {
    const sorted = [...cats].sort((a, b) => (b.is_public === true ? 1 : 0) - (a.is_public === true ? 1 : 0));
    return sorted.map((category) => {
      const isExpanded = expandedCategories.includes(category.id);
      const isActive = location.pathname === `/uadmin/categories/${category.id}`;

      return (
        <div key={category.id} className={cn(level > 0 && 'ml-4')}>
          <div className="flex items-center group">
            {category.children?.length > 0 ? (
              <button
                onClick={() => toggleCategoryExpansion(category.id)}
                className="p-1 mr-1 text-slate-400 hover:text-slate-600 rounded transition-colors flex-shrink-0"
              >
                {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
            ) : (
              <span className="w-6 flex-shrink-0" />
            )}

            <NavLink
              to={`/uadmin/categories/${category.id}`}
              className={cn(
                'flex-1 flex items-center justify-between px-2 py-1.5 text-sm font-medium rounded-md transition-colors min-w-0',
                isActive
                  ? 'bg-sky-50 text-sky-600'
                  : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
              )}
            >
              <span className="truncate">{category.name}</span>
            </NavLink>
          </div>

          {isExpanded && category.children?.length > 0 && (
            <div className="mt-1 ml-2 pl-2 border-l border-slate-200">
              {renderCategories(category.children, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  const actualWidth = collapsed ? COLLAPSED_WIDTH : width;

  return (
    <aside
      className={cn(
        'relative flex flex-col h-[100dvh] z-40 bg-white flex-shrink-0 group/sidebar',
        // 重要：必须使用 md:relative 而非 md:static，否则 absolute 定位的拖拽手柄会相对于视口而非侧边栏定位
        'fixed inset-y-0 left-0 transform md:relative md:transform-none',
        mobileOpen ? 'translate-x-0' : '-translate-x-full',
        'md:translate-x-0',
        !isResizing && 'transition-[width] duration-200 ease-out'
      )}
      style={{ width: actualWidth }}
    >
      {/* Brand Header */}
      <div className={cn(
        "h-16 flex items-center flex-shrink-0 border-b border-slate-200",
        collapsed ? "justify-center px-2" : "px-5"
      )}>
        {collapsed ? (
          /* 折叠状态：显示展开按钮（替代Logo） */
          <button
            onClick={onToggleCollapse}
            className="p-2 text-slate-400 hover:text-sky-500 hover:bg-slate-100 rounded-lg transition-colors"
            title="展开侧边栏 (⌘+B)"
          >
            <PanelLeft size={20} />
          </button>
        ) : (
          /* 展开状态：显示Logo和折叠按钮 */
          <>
            <a href="/uadmin" className="flex items-center">
              <img 
                src="/logo_24.svg" 
                alt="Unilumin FS" 
                className="h-6 object-contain"
              />
            </a>
            
            {/* 折叠按钮 - 在 header 右侧，悬停时显示 */}
            <button
              onClick={onToggleCollapse}
              className="ml-auto p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors opacity-0 group-hover/sidebar:opacity-100"
              title="收起侧边栏 (⌘+B)"
            >
              <PanelLeftClose size={18} />
            </button>
          </>
        )}
      </div>

      {showUploadModal && (
        <UploadModal onClose={() => setShowUploadModal(false)} />
      )}

      {showCreateCategoryModal && (
        <CreateCategoryModal onClose={() => setShowCreateCategoryModal(false)} />
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1 custom-scrollbar">
        <SidebarLink
          to="/uadmin/"
          icon={<Home size={20} />}
          label={t('dashboard')}
          collapsed={collapsed}
          end
        />
        <SidebarLink
          to="/uadmin/files"
          icon={<FileText size={20} />}
          label={t('allFiles')}
          collapsed={collapsed}
        />
        <SidebarLink
          to="/uadmin/starred"
          icon={<Star size={20} />}
          label={t('starred')}
          collapsed={collapsed}
        />
        <SidebarLink
          to="/uadmin/recent"
          icon={<Clock size={20} />}
          label={t('recent')}
          collapsed={collapsed}
        />
        <SidebarLink
          to="/uadmin/tags"
          icon={<Tag size={20} />}
          label={t('tags')}
          collapsed={collapsed}
        />

        {/* Categories Section */}
        {!collapsed && (
          <div className="pt-6 px-3">
            <div 
              className="flex items-center justify-between text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 cursor-pointer group"
              onClick={() => setCategoriesExpanded(!categoriesExpanded)}
            >
              <span className="flex items-center gap-1">
                {t('categories')} 
                <ChevronDown size={14} className={cn('transition-transform', !categoriesExpanded && '-rotate-90')} />
              </span>
              <button 
                onClick={(e) => { e.stopPropagation(); setShowCreateCategoryModal(true); }}
                className="hover:text-sky-500 transition-colors"
              >
                <Plus size={16} />
              </button>
            </div>
            
            {categoriesExpanded && (
              <div className="space-y-0.5 border-l border-slate-200 ml-2 pl-2">
                {renderCategories(categories)}
              </div>
            )}
          </div>
        )}

        {/* Collapsed mode system links */}
        {collapsed && (
          <div className="space-y-1 pt-4">
            <SidebarLink
              to="/uadmin/trash"
              icon={<Trash size={20} />}
              label={t('trash')}
              collapsed={collapsed}
            />
            <SidebarLink
              to="/uadmin/settings"
              icon={<Settings size={20} />}
              label={t('settings')}
              collapsed={collapsed}
            />
          </div>
        )}
      </nav>

      {/* 系统设置 - 底部可展开区域 */}
      <div className="border-t border-slate-200 mt-auto">
        {!collapsed ? (
          <div className="p-3">
            <button
              onClick={() => setSystemSettingsExpanded(!systemSettingsExpanded)}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all',
                systemSettingsExpanded 
                  ? 'bg-sky-50 text-sky-600' 
                  : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
              )}
            >
              <Cog size={20} className={cn('transition-transform flex-shrink-0', systemSettingsExpanded && 'rotate-90')} />
              <span className="truncate">系统设置</span>
              <ChevronDown 
                size={16} 
                className={cn(
                  'ml-auto transition-transform flex-shrink-0',
                  systemSettingsExpanded ? 'rotate-180' : ''
                )} 
              />
            </button>
            
            {systemSettingsExpanded && (
              <div className="mt-2 ml-2 pl-2 border-l border-slate-200 space-y-1">
                <SidebarLink
                  to="/uadmin/trash"
                  icon={<Trash size={18} />}
                  label={t('trash')}
                  collapsed={collapsed}
                />
                <SidebarLink
                  to="/uadmin/settings"
                  icon={<Settings size={18} />}
                  label={t('settings')}
                  collapsed={collapsed}
                />
                {(currentUser?.role === 'super_admin' || currentUser?.role === 'admin' || currentUser?.role === 'uploader') && (
                  <SidebarLink
                    to="/uadmin/orders"
                    icon={<Package size={18} />}
                    label="订单管理"
                    collapsed={collapsed}
                  />
                )}
                {(currentUser?.role === 'super_admin' || currentUser?.role === 'admin') && (
                  <SidebarLink
                    to="/uadmin/users"
                    icon={<Users size={18} />}
                    label="用户管理"
                    collapsed={collapsed}
                  />
                )}
                {(currentUser?.is_faq_admin || currentUser?.role === 'super_admin') && (
                  <SidebarLink
                    to="/faq/uadmin"
                    icon={<HelpCircle size={18} />}
                    label="FAQ 知识中心"
                    collapsed={collapsed}
                  />
                )}
                {canManageFaqSpaces && (
                  <SidebarLink
                    to="/uadmin/faq-spaces"
                    icon={<Layers3 size={18} />}
                    label="FAQ 页面管理"
                    collapsed={collapsed}
                  />
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="p-2">
            <NavLink
              to="/uadmin/settings"
              className={({ isActive }) => cn(
                'flex items-center justify-center p-2 rounded-lg transition-colors',
                isActive
                  ? 'bg-sky-50 text-sky-600'
                  : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
              )}
              title={t('settings')}
            >
              <Cog size={20} />
            </NavLink>
          </div>
        )}
      </div>

      {/* 拖拽调整宽度的手柄 - Apple Finder 风格 */}
      {/* 重要：手柄必须完全在侧边栏内部，因为父容器 LayoutNew 有 overflow-hidden */}
      <div
        onMouseDown={handleMouseDown}
        onDoubleClick={handleDoubleClick}
        onMouseEnter={() => setIsHoveringResizer(true)}
        onMouseLeave={() => setIsHoveringResizer(false)}
        className={cn(
          // 仅在桌面端显示，绝对定位贴着右边缘
          'hidden md:block absolute top-0 right-0 h-full z-50',
          // 宽度 4px，鼠标变为左右调整箭头
          'w-1 cursor-col-resize',
          // 默认透明，悬停/拖拽时显示蓝色高亮
          'transition-colors duration-150',
          (isHoveringResizer || isResizing) ? 'bg-sky-400' : 'hover:bg-sky-300'
        )}
        title="拖拽调整宽度，双击重置"
      />

      {/* 拖拽时的全屏透明遮罩，确保鼠标移动到窗口边缘时仍能捕获事件 */}
      {isResizing && (
        <div className="fixed inset-0 z-40 cursor-col-resize" />
      )}
    </aside>
  );
};

export default SidebarNew;
